import {bracketedCurriculumCode, sharedCurriculumPrefix} from 'common/helpers/curriculumDisplay'

import type {WizardData} from '../index'
import type {StoredSchoolSystemDetails} from '../schoolSystemDetailsCache'
import {lookupSubjectId, subjectIdKey} from './levelCurriculum'

/**
 * The Academic Structure review page works on ONE shape — a curriculum with an ordered list of
 * levels, each level owning its classes (with per-class sections) and its subjects. The wizard's
 * `manualData` spreads that same information over half a dozen fields (flattened `classes[]`
 * rows, `subjects[]` rows, `levelsByCurriculum[].levelDetails`, `subjectSelectionsByLevel`,
 * `classesReviewRowsByBranch`, the global section nomenclature) because the old multi-screen
 * flow and the bulk-import payload builder consume it that way.
 *
 * `buildAcademicStructure` folds `manualData` into the review shape; `applyAcademicStructure`
 * unfolds an edited structure back into every consumer field at once so nothing downstream
 * (Teacher Allocation, the bulk-import payload, resume) sees a partial update.
 */

type ManualData = WizardData['manualData']

export type LevelClassDraft = {
  id: string
  name: string
  sections: string[]
}

export type LevelDraft = {
  id: string
  name: string
  classes: LevelClassDraft[]
  subjects: string[]
}

export type CurriculumStructure = {
  curriculum: string
  levels: LevelDraft[]
  /** True when the levels came from the fetched curriculum defaults, not from wizard state. */
  seededFromDetails: boolean
}

/** Sections a brand-new class starts with when the tenant has no section convention yet. */
export const DEFAULT_SECTION_NAMES: readonly string[] = ['A', 'B']

const KEY_SEP = '|||'

const trim = (value: unknown): string => String(value ?? '').trim()

export const uniqueStrings = (values: readonly unknown[]): string[] =>
  Array.from(new Set(values.map(trim).filter(Boolean)))

const naturalCompare = (a: string, b: string) =>
  a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'})

const lower = (value: unknown) => trim(value).toLowerCase()

export const subjectLevelKey = (curriculum: string, level: string) =>
  `${curriculum}${KEY_SEP}${level}`

const slug = (name: string) =>
  lower(name)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/** Stable id for a level: the persisted one when present, else derived from its name. */
const levelIdFor = (name: string, id: unknown, taken: Set<string>): string => {
  let candidate = trim(id) || `lvl-${slug(name) || 'level'}`
  let suffix = 2
  while (taken.has(candidate)) {
    candidate = `${trim(id) || `lvl-${slug(name) || 'level'}`}-${suffix}`
    suffix += 1
  }
  taken.add(candidate)
  return candidate
}

let draftCounter = 0
/** Ids for levels/classes created in the drawer — unique per session, never used as data. */
export const newDraftId = (prefix: string): string => {
  draftCounter += 1
  return `${prefix}-${Date.now().toString(36)}-${draftCounter.toString(36)}`
}

/** Section label for a 0-based index: A … Z, then AA, AB … */
export const sectionLabel = (index: number): string => {
  let n = index
  let label = ''
  do {
    label = String.fromCharCode(65 + (n % 26)) + label
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return label
}

/**
 * Resize a section list to `count`, keeping the existing names and continuing the alphabet
 * for any new slots (skipping names already in use).
 */
export const resizeSections = (sections: readonly string[], count: number): string[] => {
  const safeCount = Math.max(0, Math.floor(count))
  const kept = uniqueStrings(sections).slice(0, safeCount)
  const used = new Set(kept.map(lower))
  let index = 0
  while (kept.length < safeCount) {
    const candidate = sectionLabel(index)
    index += 1
    if (used.has(candidate.toLowerCase())) continue
    used.add(candidate.toLowerCase())
    kept.push(candidate)
  }
  return kept
}

/** Parse a comma-separated "A, B, C" section-names field. */
export const parseSectionNames = (raw: string): string[] => uniqueStrings(raw.split(','))

/**
 * The section names a new class starts with: the tenant's saved section convention when one
 * exists (set by an earlier session or rebuilt from the server on resume), else A/B.
 */
export const defaultSectionsFor = (manualData: ManualData | undefined): string[] => {
  const names = parseSectionNames(trim(manualData?.classesSectionNomenclature))
  const count = Number.parseInt(trim(manualData?.classesSectionCount), 10)
  if (names.length === 0) return [...DEFAULT_SECTION_NAMES]
  if (Number.isFinite(count) && count > 0) return resizeSections(names, count)
  return names
}

const subjectsOfClasses = (classes: ReadonlyArray<{subjects?: unknown}> | undefined): string[] =>
  uniqueStrings(
    (classes ?? []).flatMap((entry) => (Array.isArray(entry?.subjects) ? entry.subjects : []))
  )

/**
 * Levels for a curriculum straight from the fetched curriculum defaults (school-system details):
 * one level per entry, its classes with the default sections, its subjects = the union of its
 * classes' subjects.
 */
export const levelsFromDetails = (
  curriculum: string,
  details: StoredSchoolSystemDetails,
  defaultSections: readonly string[]
): LevelDraft[] => {
  const taken = new Set<string>()
  return Object.entries(details[curriculum] ?? {})
    .filter(([name]) => trim(name).length > 0)
    .map(([name, detail]) => {
      const id = levelIdFor(name, undefined, taken)
      return {
        id,
        name: trim(name),
        classes: (detail?.classes ?? [])
          .filter((entry) => trim(entry?.name).length > 0)
          .map((entry, index) => ({
            id: `${id}-cls-${index}`,
            name: trim(entry.name),
            sections: [...defaultSections],
          })),
        subjects: subjectsOfClasses(detail?.classes),
      }
    })
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
const isEmptyList = (value: unknown) =>
  value === undefined || (Array.isArray(value) && value.length === 0)
const isEmptyMap = (value: unknown) =>
  value === undefined || (isPlainObject(value) && Object.keys(value).length === 0)

/**
 * A `levelsByCurriculum` entry that holds no level yet — no levels, no per-branch levels and no
 * level details (what the old Institution Setup screen wrote before any level was known). It gets
 * the curriculum's catalogue defaults, the same as a missing entry. Same rule as node-api's
 * `isPlaceholderLevels` (onboarding/wizardPreferences.js); the page has to apply it itself, since
 * it does not ask the server again in the same session.
 */
export const isPlaceholderLevels = (entry: unknown): boolean => {
  if (!isPlainObject(entry)) return false
  if (!isEmptyList(entry.levels)) return false
  if (!isEmptyMap(entry.levelDetails)) return false
  const perBranch = entry.levelsPerBranch
  if (perBranch === undefined) return true
  return isPlainObject(perBranch) && Object.values(perBranch).every(isEmptyList)
}

/**
 * Fold `manualData` into the review shape. Reads, in order of authority:
 *  - level order/ids: `levelsByCurriculum[c].levels` (+ per-branch lists for legacy layouts);
 *    a curriculum with NO entry, or a placeholder one (`isPlaceholderLevels`), falls back to the
 *    fetched defaults (`seededFromDetails`);
 *  - class names: `levelDetails`, else the flattened `classes[]` rows, else the defaults;
 *  - per-class sections: the flattened `classes[]` rows, else the review rows, else the tenant's
 *    section convention;
 *  - subjects: `subjectSelectionsByLevel`, else `levelDetails` class subjects, else the defaults.
 */
export const buildAcademicStructure = (
  manualData: ManualData | undefined,
  details: StoredSchoolSystemDetails
): CurriculumStructure[] => {
  const md = manualData ?? ({} as ManualData)
  const curriculums = uniqueStrings(md.curriculums ?? [])
  const lbc = (md.levelsByCurriculum ?? {}) as Record<string, any>
  const classRows = ((md.classes ?? []) as any[]).filter((row) => trim(row?.name).length > 0)
  const reviewRows = Object.values(
    ((md as any).classesReviewRowsByBranch ?? {}) as Record<string, any[]>
  ).flat()
  const selections = (md.subjectSelectionsByLevel ?? {}) as Record<string, string[]>
  const defaultSections = defaultSectionsFor(md)

  // Sections per (curriculum, level, grade) from the flattened rows. Rows written before the
  // curriculum was tracked carry none — those match on level + grade alone.
  const sectionsByGrade = new Map<string, Set<string>>()
  classRows.forEach((row) => {
    const grade = trim(row?.grade) || trim(row?.name).split('-')[0]
    const section = trim(row?.section)
    if (!grade || !section) return
    const keys = [
      `${lower(row?.curriculum)}${KEY_SEP}${lower(row?.level)}${KEY_SEP}${lower(grade)}`,
      `${KEY_SEP}${lower(row?.level)}${KEY_SEP}${lower(grade)}`,
    ]
    keys.forEach((key) => {
      const set = sectionsByGrade.get(key) ?? new Set<string>()
      set.add(section)
      sectionsByGrade.set(key, set)
    })
  })
  const reviewSectionsByGrade = new Map<string, string[]>()
  reviewRows.forEach((row) => {
    const key = `${lower(row?.curriculum)}${KEY_SEP}${lower(row?.level)}${KEY_SEP}${lower(row?.gradeName)}`
    if (!reviewSectionsByGrade.has(key)) {
      reviewSectionsByGrade.set(key, uniqueStrings(row?.sections ?? []))
    }
  })
  const sectionsFor = (curriculum: string, level: string, grade: string): string[] => {
    const exact = sectionsByGrade.get(
      `${lower(curriculum)}${KEY_SEP}${lower(level)}${KEY_SEP}${lower(grade)}`
    )
    const loose = sectionsByGrade.get(`${KEY_SEP}${lower(level)}${KEY_SEP}${lower(grade)}`)
    const fromRows = exact ?? loose
    if (fromRows && fromRows.size > 0) return Array.from(fromRows).sort(naturalCompare)
    const fromReview = reviewSectionsByGrade.get(
      `${lower(curriculum)}${KEY_SEP}${lower(level)}${KEY_SEP}${lower(grade)}`
    )
    if (fromReview && fromReview.length > 0) return fromReview
    return [...defaultSections]
  }

  return curriculums.map((curriculum) => {
    const cfg = lbc[curriculum]
    // A missing entry and a placeholder one (no level of any kind yet) both start from the
    // catalogue defaults.
    if (cfg === undefined || isPlaceholderLevels(cfg)) {
      return {
        curriculum,
        levels: levelsFromDetails(curriculum, details, defaultSections),
        seededFromDetails: true,
      }
    }

    // Ordered level list: the global list first, then any level that only exists in a branch
    // list (legacy "different levels per branch" layouts), then any level that only exists in
    // flattened rows (a server-side level the config never recorded).
    const ordered: Array<{name: string; id?: unknown}> = []
    const seen = new Set<string>()
    const push = (name: unknown, id?: unknown) => {
      const clean = trim(name)
      if (!clean || seen.has(clean.toLowerCase())) return
      seen.add(clean.toLowerCase())
      ordered.push({name: clean, id})
    }
    ;(Array.isArray(cfg?.levels) ? cfg.levels : []).forEach((level: any) =>
      push(level?.name, level?.id)
    )
    Object.values((cfg?.levelsPerBranch ?? {}) as Record<string, unknown[]>).forEach((names) =>
      (Array.isArray(names) ? names : []).forEach((name) => push(name))
    )
    classRows
      .filter((row) => lower(row?.curriculum) === lower(curriculum))
      .forEach((row) => push(row?.level))

    const takenIds = new Set<string>()
    const levelDetails = (cfg?.levelDetails ?? {}) as Record<string, any>
    const levels: LevelDraft[] = ordered.map(({name, id}) => {
      const levelId = levelIdFor(name, id, takenIds)
      const detail = levelDetails[name]
      const detailClasses: any[] = Array.isArray(detail?.classes) ? detail.classes : []

      let classNames = uniqueStrings(detailClasses.map((entry) => entry?.name))
      if (classNames.length === 0) {
        classNames = uniqueStrings(
          classRows
            .filter(
              (row) =>
                lower(row?.level) === lower(name) &&
                (!trim(row?.curriculum) || lower(row?.curriculum) === lower(curriculum))
            )
            .map((row) => trim(row?.grade) || trim(row?.name).split('-')[0])
        )
      }
      if (classNames.length === 0) {
        classNames = uniqueStrings(
          (details[curriculum]?.[name]?.classes ?? []).map((entry) => entry?.name)
        )
      }

      let subjects = uniqueStrings(selections[subjectLevelKey(curriculum, name)] ?? [])
      if (subjects.length === 0) subjects = subjectsOfClasses(detailClasses)
      if (subjects.length === 0) subjects = subjectsOfClasses(details[curriculum]?.[name]?.classes)

      return {
        id: levelId,
        name,
        classes: classNames.map((className, index) => ({
          id: `${levelId}-cls-${index}`,
          name: className,
          sections: sectionsFor(curriculum, name, className),
        })),
        subjects,
      }
    })

    return {curriculum, levels, seededFromDetails: false}
  })
}

/**
 * Every subject a curriculum can offer, for the drawer's chip list: the fetched defaults across
 * all of its levels, plus anything already selected on one of its levels (custom subjects).
 */
export const subjectPoolForCurriculum = (
  curriculum: string,
  structure: CurriculumStructure | undefined,
  details: StoredSchoolSystemDetails
): string[] =>
  uniqueStrings([
    ...Object.values(details[curriculum] ?? {}).flatMap((detail) =>
      subjectsOfClasses(detail?.classes)
    ),
    ...(structure?.levels ?? []).flatMap((level) => level.subjects),
  ])

export type LevelIssue = 'name' | 'classes' | 'subjects'

/** What stops a level from being saved / the page from advancing. Empty = valid. */
export const levelIssues = (level: LevelDraft): LevelIssue[] => {
  const issues: LevelIssue[] = []
  if (!trim(level.name)) issues.push('name')
  const validClasses = level.classes.filter(
    (cls) => trim(cls.name).length > 0 && uniqueStrings(cls.sections).length > 0
  )
  if (validClasses.length === 0) issues.push('classes')
  if (uniqueStrings(level.subjects).length === 0) issues.push('subjects')
  return issues
}

/**
 * Level names used more than once WITHIN one curriculum (lower-cased, trimmed). Each curriculum
 * is validated on its own: the same name under two boards is fine, since wizard-added levels
 * are stored with the curriculum prefix ("FBISE Primary" vs "CAIE Primary").
 */
export const duplicateLevelNames = (entry: CurriculumStructure): Set<string> => {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  entry.levels.forEach((level) => {
    const name = lower(level.name)
    if (!name) return
    if (seen.has(name)) duplicates.add(name)
    seen.add(name)
  })
  return duplicates
}

export const isStructureComplete = (structures: readonly CurriculumStructure[]): boolean =>
  structures.length > 0 &&
  structures.every((entry) => duplicateLevelNames(entry).size === 0) &&
  structures.every(
    (entry) =>
      entry.levels.length > 0 && entry.levels.every((level) => levelIssues(level).length === 0)
  )

/**
 * The prefix a class added under `curriculum` gets in front of its name, chosen to match the
 * classes already there: the first word every existing class name of that curriculum starts
 * with ("FBISE" for "FBISE Pre-primary 1", "FBISE Pre-primary 2"); otherwise the bracketed short
 * form of the curriculum name ("Federal Board (FBISE)" → "FBISE"); otherwise the name itself
 * (a custom curriculum typed as "BISE Punjab" → "BISE Punjab").
 */
export const classNamePrefixFor = (
  curriculum: string,
  structure: CurriculumStructure | undefined
): string => {
  const classNames = (structure?.levels ?? []).flatMap((level) =>
    level.classes.map((cls) => cls.name)
  )
  return (
    sharedCurriculumPrefix(curriculum, classNames) ||
    bracketedCurriculumCode(curriculum) ||
    trim(curriculum)
  )
}

/**
 * Case-insensitive identity of a class row: name + branch + level + curriculum. Two curricula
 * can both have "Grade 1-A" under a level called "Primary". A row without a curriculum (legacy
 * state) has an empty curriculum segment; callers comparing it with curriculum-bearing rows also
 * try that form (see `classIdentities`).
 */
export const classIdentity = (
  name: unknown,
  branch: unknown,
  level: unknown,
  curriculum?: unknown
) => [lower(name), lower(branch), lower(level), lower(curriculum)].join(KEY_SEP)

/** A row's identity with its curriculum, and without it (matches legacy rows that had none). */
export const classIdentities = (
  name: unknown,
  branch: unknown,
  level: unknown,
  curriculum?: unknown
): string[] =>
  Array.from(
    new Set([classIdentity(name, branch, level, curriculum), classIdentity(name, branch, level)])
  )

/** Case-insensitive identity of a level within a curriculum. */
export const levelIdentity = (curriculum: unknown, name: unknown) =>
  [lower(curriculum), lower(name)].join(KEY_SEP)

/**
 * Unfold the review structure back into every `manualData` field that describes classes and
 * subjects. Idempotent: applying an unchanged structure yields the same fields. Preserves server
 * ids on classes/subjects that still exist, records removed classes in `deletedClasses` so the
 * next bulk-import deletes them, and keeps the tenant's section convention in step with what
 * the classes actually use.
 */
export const applyAcademicStructure = (
  manualData: ManualData | undefined,
  structures: readonly CurriculumStructure[]
): Partial<ManualData> => {
  const md = manualData ?? ({} as ManualData)
  const branches = uniqueStrings(
    ((md.branches ?? []) as unknown[]).map((entry: any) =>
      typeof entry === 'string' ? entry : entry?.name
    )
  )
  const effectiveBranches = branches.length > 0 ? branches : ['Main Branch']

  const prevClasses = ((md.classes ?? []) as any[]).filter((row) => trim(row?.name).length > 0)
  const classIdByIdentity = new Map<string, number>()
  prevClasses.forEach((row) => {
    const id = Number(row?.id)
    if (Number.isFinite(id) && id > 0) {
      classIdByIdentity.set(classIdentity(row?.name, row?.branch, row?.level, row?.curriculum), id)
    }
  })

  const prevSubjects = ((md.subjects ?? []) as any[]).filter((row) => trim(row?.name).length > 0)
  const subjectIdByNameAndClass: Record<string, number> = {
    ...(((md as any).subjectIdByNameAndClass as Record<string, number>) ?? {}),
  }
  prevSubjects.forEach((row) => {
    const id = Number(row?.id)
    if (Number.isFinite(id) && id > 0 && trim(row?.class)) {
      subjectIdByNameAndClass[subjectIdKey(row.name, row.class, row.curriculum, row.branch)] ??= id
    }
  })

  const existingLbc = (md.levelsByCurriculum ?? {}) as Record<string, any>
  const levelsByCurriculum: Record<string, any> = {...existingLbc}
  const classes: any[] = []
  const subjects: any[] = []
  const subjectSeen = new Set<string>()
  const classesReviewRowsByBranch: Record<string, any[]> = {}
  const subjectSelectionsByLevel: Record<string, string[]> = {}
  const allSectionNames = new Set<string>()
  let levelsCount = 0
  // Removed levels: a level the previous config had and this structure no longer does goes out
  // as a delete on the next sync (its own request, after the classes one); one that came back
  // leaves the queue. Recorded by name — the payload builder resolves the server id per branch
  // from branchLevelIdMap and skips levels that were never synced.
  const currentLevelIdentities = new Set<string>()
  const deletedLevels: NonNullable<ManualData['deletedLevels']> = []
  const deletedLevelSeen = new Set<string>()
  const pushDeletedLevel = (curriculum: string, name: string) => {
    const identity = levelIdentity(curriculum, name)
    if (currentLevelIdentities.has(identity) || deletedLevelSeen.has(identity)) return
    deletedLevelSeen.add(identity)
    deletedLevels.push({curriculum, name})
  }

  structures.forEach(({curriculum, levels}) =>
    levels.forEach((level) => {
      if (trim(level.name)) currentLevelIdentities.add(levelIdentity(curriculum, level.name))
    })
  )

  structures.forEach(({curriculum, levels, seededFromDetails}) => {
    // A curriculum still waiting on its defaults (no entry, nothing fetched yet) must not get
    // an empty entry written — that would read as "seeded" and block the prefill forever.
    if (seededFromDetails && levels.length === 0) return
    const cfg = existingLbc[curriculum] ?? {}
    const levelNames = levels.map((level) => trim(level.name)).filter(Boolean)
    const previousLevelNames = ((Array.isArray(cfg?.levels) ? cfg.levels : []) as any[])
      .map((level) => trim(level?.name))
      .filter(Boolean)
    const previousNames = new Set(previousLevelNames.map((name) => name.toLowerCase()))
    previousLevelNames.forEach((name) => pushDeletedLevel(curriculum, name))

    // Which branches carry which levels. The legacy "different levels per branch" layout keeps
    // its per-branch lists (pruned of removed levels, extended with new ones); everything else
    // gives every branch every level.
    const perBranch: Record<string, string[]> = {}
    const perBranchLegacy = cfg?.levelsSameAcrossBranches === false && cfg?.levelsPerBranch
    effectiveBranches.forEach((branch) => {
      if (perBranchLegacy) {
        const kept = new Set(
          ((cfg.levelsPerBranch?.[branch] ?? []) as unknown[]).map((name) => lower(name))
        )
        perBranch[branch] = levelNames.filter(
          (name) => kept.has(name.toLowerCase()) || !previousNames.has(name.toLowerCase())
        )
      } else {
        perBranch[branch] = [...levelNames]
      }
    })

    const levelDetails: Record<string, {classes: Array<{name: string; subjects: string[]}>}> = {}
    levels.forEach((level) => {
      const levelName = trim(level.name)
      if (!levelName) return
      levelsCount += 1
      const levelSubjects = uniqueStrings(level.subjects)
      const levelClasses = level.classes
        .map((cls) => ({name: trim(cls.name), sections: uniqueStrings(cls.sections)}))
        .filter((cls) => cls.name.length > 0)
      const seenClassNames = new Set<string>()
      const dedupedClasses = levelClasses.filter((cls) => {
        if (seenClassNames.has(cls.name.toLowerCase())) return false
        seenClassNames.add(cls.name.toLowerCase())
        return true
      })

      levelDetails[levelName] = {
        classes: dedupedClasses.map((cls) => ({name: cls.name, subjects: [...levelSubjects]})),
      }
      subjectSelectionsByLevel[subjectLevelKey(curriculum, levelName)] = levelSubjects

      effectiveBranches.forEach((branch) => {
        if (!perBranch[branch].includes(levelName)) return
        const rows = (classesReviewRowsByBranch[branch] ??= [])
        dedupedClasses.forEach((cls) => {
          const sections = cls.sections.length > 0 ? cls.sections : ['A']
          sections.forEach((section) => {
            allSectionNames.add(section)
            const className = `${cls.name}-${section}`
            const id =
              classIdByIdentity.get(classIdentity(className, branch, levelName, curriculum)) ??
              classIdByIdentity.get(classIdentity(className, branch, levelName))
            classes.push({
              ...(id ? {id} : {}),
              branch,
              level: levelName,
              curriculum,
              grade: cls.name,
              section,
              teacher: '',
              name: className,
            })
            levelSubjects.forEach((subjectName) => {
              const key = [subjectName, className, branch, levelName].join(KEY_SEP)
              if (subjectSeen.has(key)) return
              subjectSeen.add(key)
              const subjectId = lookupSubjectId(
                subjectIdByNameAndClass,
                subjectName,
                className,
                curriculum,
                branch
              )
              subjects.push({
                ...(subjectId ? {id: subjectId} : {}),
                name: subjectName,
                curriculum,
                level: levelName,
                branch,
                class: className,
                teacher: '',
                frequency: '',
                duration: '',
              })
            })
          })
          rows.push({
            id: `${branch}-${curriculum}-${levelName}-${cls.name}`,
            curriculum,
            level: levelName,
            gradeName: cls.name,
            sections,
            subjects: [...levelSubjects],
          })
        })
      })
    })

    levelsByCurriculum[curriculum] = {
      ...cfg,
      levels: levels
        .filter((level) => trim(level.name).length > 0)
        .map((level) => ({name: trim(level.name), id: level.id})),
      levelsSameAcrossBranches: cfg?.levelsSameAcrossBranches ?? true,
      levelsPerBranch: perBranch,
      levelDetails,
    }
  })

  // Removed classes: anything that existed before and is gone now goes out as a delete on the
  // next sync; anything that came back is dropped from the pending deletes.
  const presentIdentities = new Set(
    classes.flatMap((row) => classIdentities(row.name, row.branch, row.level, row.curriculum))
  )
  const deletedClasses: NonNullable<ManualData['deletedClasses']> = []
  const deletedSeen = new Set<string>()
  const pushDeleted = (row: any) => {
    // A class without a server id was created only in the local wizard draft. If it is
    // removed before the first bulk-import, there is nothing persisted to delete. Rows hydrated
    // from GET /onboarding/data carry no id either but ARE persisted (`persisted: true`).
    const id = Number(row?.id)
    const hasId = Number.isFinite(id) && id > 0
    if (!hasId && row?.persisted !== true) return
    const identity = classIdentity(row?.name, row?.branch, row?.level, row?.curriculum)
    if (presentIdentities.has(identity) || deletedSeen.has(identity)) return
    deletedSeen.add(identity)
    deletedClasses.push({
      grade: trim(row?.grade) || trim(row?.name).split('-')[0],
      section: trim(row?.section),
      name: trim(row?.name),
      branch: trim(row?.branch) || undefined,
      level: trim(row?.level),
      curriculum: trim(row?.curriculum) || undefined,
      ...(hasId ? {id} : {}),
      ...(row?.persisted === true ? {persisted: true} : {}),
    })
  }
  ;((md.deletedClasses ?? []) as any[]).forEach(pushDeleted)
  prevClasses.forEach(pushDeleted)
  // Levels already queued (restored from the classes draft, or removed on Institution Setup) stay
  // queued unless they came back. One removed from a single branch came back only if that branch
  // lists it again — the other branches still have it.
  ;((md.deletedLevels ?? []) as any[]).forEach((entry) => {
    const branch = trim(entry?.branch)
    if (!branch) {
      pushDeletedLevel(trim(entry?.curriculum), trim(entry?.name))
      return
    }
    const backInBranch = Object.entries(levelsByCurriculum).some(
      ([curriculum, cfg]) =>
        (!trim(entry?.curriculum) || lower(curriculum) === lower(entry.curriculum)) &&
        Object.entries((cfg?.levelsPerBranch ?? {}) as Record<string, unknown[]>).some(
          ([name, levels]) =>
            lower(name) === lower(branch) &&
            (levels ?? []).some((level) => lower(level) === lower(entry?.name))
        )
    )
    const identity = `${levelIdentity(entry?.curriculum, entry?.name)}${KEY_SEP}${lower(branch)}`
    if (backInBranch || deletedLevelSeen.has(identity)) return
    deletedLevelSeen.add(identity)
    deletedLevels.push({curriculum: trim(entry?.curriculum), name: trim(entry?.name), branch})
  })

  const sectionNames = Array.from(allSectionNames).sort(naturalCompare)

  return {
    levelsByCurriculum,
    levelsCount,
    levels: structures.flatMap((entry) =>
      entry.levels.map((level) => ({name: trim(level.name)})).filter((level) => level.name)
    ),
    classes,
    classesCount: classes.length,
    subjects,
    subjectsCount: subjects.length,
    subjectSelectionsByLevel,
    subjectIdByNameAndClass,
    classesReviewRowsByBranch,
    deletedClasses,
    deletedLevels,
    ...(sectionNames.length > 0
      ? {
          classesSectionCount: String(sectionNames.length),
          classesSectionNomenclature: sectionNames.join(','),
        }
      : {}),
    // The old Classes flow's final screen — a legacy deep link lands on the review either way.
    classesFlowStep: 3,
  } as Partial<ManualData>
}
